// ============================================================
// PreviewPane — delad fil-preview (PDF/bild/annat).
// Anvnds i ReceiptsCard (ReviewPopup), Import (PipelineReviewPopup,
// DupComparePopup) och DuplicateComparePopup (Feature 2).
// ============================================================

import type { CSSProperties, ReactNode } from 'react'

interface Props {
  /** URL till filen — Firebase Storage downloadUrl eller URL.createObjectURL() */
  url: string | undefined
  mime?: string
  filename: string
  /** Dropbox-sökväg när posten kom via autosync och saknar Storage-fil */
  sourcePath?: string
  style?: CSSProperties
  background?: string
  showOpenLink?: boolean
  footer?: ReactNode
}

export function PreviewPane({
  url, mime, filename, sourcePath, style, background = '#111827', showOpenLink = true, footer,
}: Props) {
  const lower = (filename || '').toLowerCase()
  const isImage = (!!mime && mime.startsWith('image/')) || /\.(png|jpe?g|gif|webp|heic)$/i.test(lower)
  const isPdf = mime === 'application/pdf' || lower.endsWith('.pdf')

  if (!url) {
    return (
      <div
        style={{
          flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
          color: 'var(--text-muted)', background, fontSize: '0.85rem', gap: 8, padding: '1rem', textAlign: 'center',
          ...style,
        }}
      >
        <div>Ingen PDF i Firebase Storage</div>
        <div style={{ fontSize: '0.75rem' }}>{filename}</div>
        {sourcePath && (
          <div style={{ fontSize: '0.7rem', wordBreak: 'break-all', maxWidth: '100%' }}>
            Dropbox-synkad: {sourcePath}
          </div>
        )}
      </div>
    )
  }

  return (
    <div
      style={{
        flex: 1, display: 'flex', flexDirection: 'column',
        background, overflow: 'hidden', ...style,
      }}
    >
      {isPdf ? (
        <iframe
          src={url}
          title={filename}
          style={{ flex: 1, border: 'none', width: '100%' }}
        />
      ) : isImage ? (
        <div style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '1rem', overflow: 'auto' }}>
          <img
            src={url}
            alt={filename}
            style={{ maxWidth: '100%', maxHeight: '100%', objectFit: 'contain', borderRadius: 4 }}
          />
        </div>
      ) : (
        <div style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'var(--text-muted)', flexDirection: 'column', gap: '0.5rem' }}>
          <span style={{ fontSize: '0.85rem' }}>Förhandsgranskning saknas</span>
          {showOpenLink && (
            <a href={url} target="_blank" rel="noopener" style={{ color: 'var(--primary)', fontSize: '0.85rem' }}>
              Öppna fil
            </a>
          )}
        </div>
      )}
      {footer}
    </div>
  )
}
